import { useEffect, useState } from 'react'
import { FocusTrap } from 'focus-trap-react'
import Cookies from 'js-cookie'
import { useTranslation } from 'react-i18next'

import { CheckoutStep, CheckoutStepViewType, CookieTypes } from '@leanpay/models'
import { checkoutService } from '@leanpay/services'
import { Button, CookieIcon, Dialog, DialogContent, DialogTrigger, Title } from '@leanpay/ui'
import { updateGoogleConsent } from '@leanpay/utils'

import { useAppStore } from 'app/app-store'

import EditCookiesDialogContent from './edit-cookies-dialog-content'

export const COOKIES_ARRAY = 'cookie_control_enabled_cookies'
export const COOKIES_CONSENT = 'cookie_control_consent'
export const COOKIE_EXPIRATION_DAYS = 365

export const RawCookies = Cookies.withConverter({
    write: (value) => value, // Don't encode the value
    read: (value) => value, // Don't decode the value
})

const ManageCookiesConsent = () => {
    const { setIsCookieConsentEnabled } = useAppStore()
    const [isOpen, setIsOpen] = useState(false)
    const { t } = useTranslation()
    const [cookieConsent, setCookieConsent] = useState(Cookies.get(COOKIES_CONSENT))

    const { vendorToken } = useAppStore()

    const setCookie = (value: string) => {
        const hostname = window.location.hostname
        const isLocalhost =
            hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('192')
        const cookieOptions = {
            expires: COOKIE_EXPIRATION_DAYS,
            ...(isLocalhost ? {} : { domain: `.${hostname.split('.').slice(-2).join('.')}` }),
        }

        RawCookies.set(COOKIES_ARRAY, value, cookieOptions)
        RawCookies.set(COOKIES_CONSENT, 'true', cookieOptions)

        // Tell Google Consent Mode about the selection — the cookies alone are
        // invisible to GTM/GA (TEC-19136).
        updateGoogleConsent(value.split(','))
    }

    const handleAcceptAllCookies = () => {
        setIsCookieConsentEnabled(true)
        setCookie(
            `${CookieTypes.Necessary},${CookieTypes.Analytical},${CookieTypes.Functional},${CookieTypes.Marketing}`,
        )
        handleRecordStep(false)
        setCookieConsent('true')
        setIsOpen(false)
    }

    const handleDeclineNonEssentialCookies = () => {
        setIsCookieConsentEnabled(true)
        setCookie(`${CookieTypes.Necessary}`)
        handleRecordStep(false)
        setCookieConsent('true')
        setIsOpen(false)
    }

    const handleSaveCookies = (cookies: string[]) => {
        setIsCookieConsentEnabled(true)
        setCookie(cookies.join(','))
        handleRecordStep(false)
        setCookieConsent('true')
        setIsOpen(false)
    }

    const handleRecordStep = (open?: boolean) => {
        const cookieConsent = RawCookies.get(COOKIES_CONSENT)
        const cookiesArray = RawCookies.get(COOKIES_ARRAY)
        const userAcceptedAllCookies = cookiesArray?.split(',').length === 4 // All cookies
        const userDeclinedNonEssentialCookies = cookiesArray?.split(',').length === 1 // Necessary cookies
        const userModifiedCookies = !userAcceptedAllCookies && !userDeclinedNonEssentialCookies

        const paramsOnEnterCookieConsent = {
            token: vendorToken,

            checkoutStep: CheckoutStep.COOKIE_CONSENT,
            checkoutStepViewType: open ? CheckoutStepViewType.OPEN : CheckoutStepViewType.CLOSE,
            data: {
                cookie_control_consent: JSON.stringify(cookieConsent),
                cookie_control_enabled_cookies: JSON.stringify(cookiesArray),
                userAcceptedAllCookies: userAcceptedAllCookies,
                userDeclinedNonEssentialCookies: userDeclinedNonEssentialCookies,
                userModifiedSelection: userModifiedCookies,
                necessaryCookiesAccepted: !!cookiesArray?.includes(CookieTypes.Necessary),
                analyticalCookiesAccepted: !!cookiesArray?.includes(CookieTypes.Analytical),
                functionalCookiesAccepted: !!cookiesArray?.includes(CookieTypes.Functional),
                marketingCookiesAccepted: !!cookiesArray?.includes(CookieTypes.Marketing),
            },
        }

        void checkoutService.recordStep(paramsOnEnterCookieConsent)
    }

    useEffect(() => {
        if (isOpen) {
            handleRecordStep(isOpen)
        }
    }, [isOpen])

    const renderCookieConsentTrigger = () => {
        if (cookieConsent) {
            return (
                <DialogTrigger asChild>
                    <Button
                        variant="link"
                        onClick={() => setIsOpen(true)}
                        className="flex items-center cursor-pointer gap-2 w-full bg-white justify-center font-normal text-sm h-auto min-h-0 [&>span]:underline [&>span]:text-black"
                    >
                        {/* <CookieIcon className="text-black" /> */}
                        <CookieIcon />
                        <span className="text-sm text-black1 uppercase">
                            {t('cookieConsent.labels.manageCookie')}
                        </span>
                    </Button>
                </DialogTrigger>
            )
        }

        return (
            <div className="flex items-center gap-4 justify-between max-h-screen overflow-y-auto max-[766px]:flex-col max-[766px]:justify-start min-[1024px]:max-w-[80%]">
                {/* Show only on mobile */}
                <div className="hidden max-[766px]:flex mt-4 gap-4 items-start">
                    <span className="text-[35px] inline-block leading-[35px]">🍪</span>
                    <Title className="text-base uppercase text-left">
                        {t('cookieConsent.title')}
                    </Title>
                </div>
                {/* Show only on desktop */}
                <div className="text-[45px] mt-8 mb-6 max-[766px]:hidden">🍪</div>
                <div className="flex items-start flex-col max-w-[50%] max-[766px]:max-w-[340px]">
                    <Title className="text-base font-bold text-black uppercase text-left max-[766px]:hidden">
                        {t('cookieConsent.title')}
                    </Title>
                    <p
                        className="text-sm [&_a]:text-left max-[766px]:text-center"
                        dangerouslySetInnerHTML={{
                            __html: t('cookieConsent.cookieDescription'),
                        }}
                    />
                </div>
                <div className="w-full flex items-center justify-between gap-4 max-[766px]:flex-col-reverse">
                    <Button
                        testId="manage-cookies"
                        onClick={() => setIsOpen(true)}
                        variant="ghost"
                        className="text-left w-full cursor-pointer underline text-black uppercase font-normal text-sm p-0 h-auto bg-transparent min-h-0 max-[766px]:text-center"
                    >
                        {t('cookieConsent.labels.manageCookie')}
                    </Button>

                    <div className="flex items-center justify-end w-full gap-4 max-[766px]:flex-col">
                        <Button
                            testId="accept-all-cookies"
                            className="w-fit min-w-[180px] max-[766px]:w-full"
                            onClick={handleAcceptAllCookies}
                            aria-label={t('cookieConsent.labels.acceptAllA11y')}
                        >
                            {t('cookieConsent.labels.acceptAll')}
                        </Button>

                        <Button
                            testId="decline-non-essential-cookies"
                            className="w-fit min-w-[180px] max-[766px]:w-full"
                            variant="outline"
                            onClick={handleDeclineNonEssentialCookies}
                            aria-label={t('cookieConsent.labels.declineNonEssentialA11y')}
                        >
                            {t('cookieConsent.labels.declineNonEssential')}
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <FocusTrap
            focusTrapOptions={{
                escapeDeactivates: false,
                allowOutsideClick: true,
            }}
            active={!cookieConsent}
        >
            <div
                className={`flex flex-col shadow-lg bg-white p-4 justify-center items-center bottom-0 rounded-lg rounded-t-none z-10 max-[766px]:w-full max-[766px]:rounded-none min-[767px]:max-[899px]:rounded-none min-[767px]:max-[899px]:w-full ${
                    cookieConsent ? 'w-full max-w-[840px] static' : 'w-full fixed'
                }`}
            >
                <Dialog
                    open={isOpen}
                    onOpenChange={(value) => {
                        setIsOpen(value)
                    }}
                    modal={true}
                >
                    {renderCookieConsentTrigger()}
                    <DialogContent showCloseIcon={false}>
                        <EditCookiesDialogContent
                            onAcceptAllCookies={handleAcceptAllCookies}
                            onDeclineNonEssentialCookies={handleDeclineNonEssentialCookies}
                            onSaveCookies={handleSaveCookies}
                        />
                    </DialogContent>
                </Dialog>
            </div>
        </FocusTrap>
    )
}

export default ManageCookiesConsent
